import React from 'react';
import reportFrontendIncident from '../../monitoring/reportFrontendIncident';

/**
 * Catches React render errors, reports them to the watchdog, and shows a
 * recoverable fallback instead of a white screen.
 */
class FrontendErrorBoundary extends React.Component {
	constructor(props) {
		super(props);
		this.state = { hasError: false, errorMessage: null };
	}

	static getDerivedStateFromError(error) {
		return {
			hasError: true,
			errorMessage: error?.message || 'Something went wrong while loading this page.',
		};
	}

	componentDidCatch(error, errorInfo) {
		reportFrontendIncident({
			type: 'react-error-boundary',
			message: error?.message || 'React render failure',
			stack: error?.stack || null,
			component_stack: errorInfo?.componentStack || null,
			metadata: { boundary: 'FrontendErrorBoundary' },
		});
	}

	componentDidUpdate(prevProps) {
		if (prevProps.resetKey !== this.props.resetKey && this.state.hasError) {
			this.setState({ hasError: false, errorMessage: null });
		}
	}

	handleReload = () => {
		window.location.reload();
	};

	render() {
		if (!this.state.hasError) {
			return this.props.children;
		}

		return (
			<div className="min-h-screen bg-slate-50 text-slate-900 flex items-center justify-center px-6">
				<div className="max-w-lg w-full rounded-2xl border border-slate-200 bg-white p-8 shadow-sm">
					<p className="text-xs font-semibold uppercase tracking-[0.24em] text-slate-500">
						App Recovery
					</p>
					<h1 className="mt-3 text-2xl font-semibold text-slate-900">
						This page hit an unexpected error.
					</h1>
					<p className="mt-3 text-sm leading-6 text-slate-600">
						The error has been recorded for review. Reload to retry.
					</p>
					{this.state.errorMessage && (
						<div className="mt-4 rounded-xl bg-slate-100 px-4 py-3 text-sm text-slate-700">
							{this.state.errorMessage}
						</div>
					)}
					<button
						type="button"
						onClick={this.handleReload}
						className="mt-6 inline-flex items-center justify-center rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white transition hover:bg-slate-700"
					>
						Reload page
					</button>
				</div>
			</div>
		);
	}
}

export default FrontendErrorBoundary;
